import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RiMenu3Line, RiCloseLine } from "react-icons/ri";
import logo from "../../assets/LogoSalesSprc.png";
import GetInvolvedModal from "../getinvolvedmodal/GetInvolvedModal";
import { scrollToSection, setScrollLocked, getLenis } from "../../lib/motion";
import "./navbar.css";

/* Section ids are unchanged from the previous site so existing deep links
   such as /#possibility keep working. */
const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "whatsprc", label: "About" },
  { id: "features", label: "Our work" },
  { id: "blog", label: "Stories" },
];

const Navbar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [progress, setProgress] = useState(0);
  const [showInvolved, setShowInvolved] = useState(false);

  const drawerRef = useRef(null);
  const toggleRef = useRef(null);

  /* Scroll state drives both the solid nav background and the reading bar. */
  useEffect(() => {
    const onScroll = () => {
      const top = window.scrollY;
      setScrolled(top > 24);
      const track =
        document.documentElement.scrollHeight - window.innerHeight;
      setProgress(track > 0 ? Math.min(100, (top / track) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Highlight whichever section currently owns the upper third of the screen. */
  useEffect(() => {
    if (!isHome) return undefined;
    const sections = NAV_ITEMS.map((item) =>
      document.getElementById(item.id)
    ).filter(Boolean);
    if (!sections.length || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (hit) setActive(hit.target.id);
      },
      { rootMargin: "-25% 0px -60% 0px", threshold: [0, 0.2, 0.6] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isHome]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  /* While the drawer is open: lock the page, close on Escape, keep Tab inside. */
  useEffect(() => {
    if (!drawerOpen) return undefined;

    document.body.classList.add("is-locked");
    setScrollLocked(true);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDrawer();
        toggleRef.current?.focus();
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusables = drawerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const timer = window.setTimeout(() => {
      drawerRef.current?.querySelector("a, button")?.focus();
    }, 60);

    return () => {
      document.body.classList.remove("is-locked");
      setScrollLocked(false);
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(timer);
    };
  }, [drawerOpen, closeDrawer]);

  const goToSection = useCallback(
    (id) => {
      closeDrawer();
      if (!isHome) {
        navigate(id === "home" ? "/" : `/#${id}`);
        return;
      }
      if (id === "home") {
        const lenis = getLenis();
        if (lenis) lenis.scrollTo(0, { duration: 1.1 });
        else window.scrollTo({ top: 0, behavior: "smooth" });
        setActive("home");
        return;
      }
      scrollToSection(id);
    },
    [closeDrawer, isHome, navigate]
  );

  const renderLinks = (variant) =>
    NAV_ITEMS.map((item) => (
      <a
        key={item.id}
        href={item.id === "home" ? "/" : `/#${item.id}`}
        className={`nav-link-sales nav-link-sales--${variant}${
          isHome && active === item.id ? " is-active" : ""
        }`}
        aria-current={isHome && active === item.id ? "page" : undefined}
        onClick={(event) => {
          event.preventDefault();
          goToSection(item.id);
        }}
      >
        {item.label}
      </a>
    ));

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {/* The bar only floats transparently over the home hero, which is dark.
          Everywhere else it stays solid so the white text keeps its contrast. */}
      <header
        className={`sales-nav${scrolled || !isHome ? " is-solid" : ""}${
          drawerOpen ? " is-drawer-open" : ""
        }`}
      >
        <div className="sales-nav__inner shell shell--wide">
          <a
            className="sales-nav__brand"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              goToSection("home");
            }}
          >
            <img src={logo} alt="" width={44} height={44} aria-hidden="true" />
            <span className="sales-nav__wordmark">
              SALES
              <span className="sales-nav__tagline">Barishal, Bangladesh</span>
            </span>
          </a>

          <nav className="sales-nav__links" aria-label="Primary">
            {renderLinks("bar")}
          </nav>

          <div className="sales-nav__actions">
            <button
              type="button"
              className="btn-sales btn-sales--ghost-dark btn-sales--sm"
              onClick={() => setShowInvolved(true)}
            >
              Get involved
            </button>
            <button
              type="button"
              className="btn-sales btn-sales--donate btn-sales--sm"
              onClick={() => goToSection("possibility")}
            >
              Donate
            </button>
          </div>

          <button
            ref={toggleRef}
            type="button"
            className="sales-nav__toggle"
            aria-expanded={drawerOpen}
            aria-controls="sales-drawer"
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
            onClick={() => setDrawerOpen((open) => !open)}
          >
            {drawerOpen ? <RiCloseLine size={26} /> : <RiMenu3Line size={26} />}
          </button>
        </div>

        <div className="sales-nav__progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${progress / 100})` }} />
        </div>
      </header>

      <div
        className={`sales-drawer-backdrop${drawerOpen ? " is-open" : ""}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      <aside
        id="sales-drawer"
        ref={drawerRef}
        className={`sales-drawer${drawerOpen ? " is-open" : ""}`}
        aria-label="Menu"
        aria-hidden={!drawerOpen}
        {...(!drawerOpen ? { inert: "" } : {})}
      >
        <nav className="sales-drawer__links" aria-label="Mobile">
          {renderLinks("drawer")}
        </nav>
        <div className="sales-drawer__actions">
          <button
            type="button"
            className="btn-sales btn-sales--donate btn-sales--block"
            onClick={() => goToSection("possibility")}
          >
            Donate
          </button>
          <button
            type="button"
            className="btn-sales btn-sales--ghost-dark btn-sales--block"
            onClick={() => {
              closeDrawer();
              setShowInvolved(true);
            }}
          >
            Get involved
          </button>
        </div>
        <div className="sales-drawer__meta">
          <a href="tel:01536243460">01536 243460</a>
          <a href="mailto:salesbarishal1988@gmail.com">
            salesbarishal1988@gmail.com
          </a>
        </div>
      </aside>

      <GetInvolvedModal
        show={showInvolved}
        onHide={() => setShowInvolved(false)}
      />
    </>
  );
};

export default Navbar;
